/**
 * Lo que comparten la lista de ofertas y la ficha.
 *
 * Vive aparte porque en cuanto la ficha se separó de la lista había dos sitios
 * escribiendo la misma regla de conversión de importes, y ésa es exactamente la
 * clase de duplicado que acaba enseñando dos precios distintos para el mismo
 * número.
 */
import {
  IconBriefcase,
  IconCalendarEvent,
  IconFile,
  IconFileDescription,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFileZip,
  IconMusic,
  IconPhoto,
  IconSchool,
  IconPackage,
  IconVideo,
} from '@tabler/icons-react'
/* El tope del catálogo se lee aquí porque aquí se decide si algo se puede
   publicar, que es lo único que ese tope frena. */
import { PLAN_LIMITS, reachedLimit } from '@webbuilder/plans'
import type { BillingPlan } from '../../../lib/api'
import type { Translate } from '@webbuilder/i18n'
import type { FulfillmentStatus, Offer, OfferPrice, OfferRevenue, Order } from '../../../lib/api'
import { moneyLocale } from '../../../lib/region'
import { activeLanguage, translate, translateServerError } from '../../../lib/i18n'

/**
 * Qué se llevó de un producto físico, en una línea: «M / Rojo × 2», «× 3» o
 * sólo la variante. Vacío en una compra de una unidad sin variante. Es la
 * misma regla que `itemSummary` del worker, que escribe los correos.
 */
export function orderItemSummary(order: Pick<Order, 'variantLabel' | 'quantity'> & { items?: Order['items'] }): string {
  /* Un pedido del carrito: cada cosa con lo suyo, «Taza · Verde × 2, Guía». */
  if (order.items?.length) {
    return order.items.map((item) => {
      const units = item.quantity > 1 ? `× ${item.quantity}` : ''
      return [item.title, [item.variantLabel?.trim() ?? '', units].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
    }).join(', ')
  }
  const units = order.quantity && order.quantity > 1 ? `× ${order.quantity}` : ''
  return [order.variantLabel?.trim() ?? '', units].filter(Boolean).join(' ')
}

/**
 * Un pedido de varios productos, para una fila de lista: cuántos son y
 * cuántas unidades en total. `null` si es de uno solo (la fila usa su nombre).
 */
export function orderProductCount(order: { items?: Order['items'] }): { products: number; units: number } | null {
  if (!order.items || order.items.length < 2) return null
  const products = new Set(order.items.map((item) => item.offerId ?? item.title)).size
  const units = order.items.reduce((sum, item) => sum + Math.max(1, item.quantity), 0)
  return products > 1 ? { products, units } : null
}

/** «5 productos · 12 unidades»; sin las unidades si es una de cada uno. */
export function orderCountTitle(count: { products: number; units: number }, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const products = t('orders.products', { count: count.products })
  return count.units > count.products ? `${products} · ${t('orders.units', { count: count.units })}` : products
}

/**
 * Los productos de un pedido, compactos: cada nombre una vez con sus unidades
 * («tes, Taza × 10»). Las variantes —talla, color— van en la ficha, que es
 * donde se prepara la caja; en la fila sólo alargaban el renglón.
 */
export function orderItemsCompact(order: { items?: Order['items'] }): string {
  const totals = new Map<string, number>()
  for (const item of order.items ?? []) totals.set(item.title, (totals.get(item.title) ?? 0) + Math.max(1, item.quantity))
  return [...totals].map(([title, units]) => (units > 1 ? `${title} × ${units}` : title)).join(', ')
}

/** Monedas sin decimales: en ellas lo escrito ya es la unidad entera. */
const ZERO_DECIMAL = new Set(['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'])

/** Cuántas unidades mínimas tiene una entera. La misma tabla que el servidor. */
export function minorUnitFactor(currency: string): number {
  return ZERO_DECIMAL.has(currency.toLowerCase()) ? 1 : 100
}

/** El símbolo corto de una moneda («$», «€», «£»), para ponerlo delante del importe. */
export function currencySymbol(currency: string): string {
  try {
    const part = new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase(), currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((piece) => piece.type === 'currency')
    return part?.value ?? ''
  } catch {
    return ''
  }
}

/**
 * "199.90" → 19990. El importe como se escribe de verdad.
 *
 * El separador de millares existe —«1,200.00» en inglés, «1.200,50» en
 * español— y la regla de antes (cambiar la PRIMERA coma por un punto) lo
 * rompía de dos maneras distintas, las dos malas:
 *
 *   · «1,200.00» quedaba en «1.200.00», que no es un número. El campo se daba
 *     por inválido y el alta no pasaba de ahí, sin decir qué tenía de malo.
 *   · «1,200» quedaba en «1.200» → 1.2, y eso no falla: guarda el servicio a
 *     mil veces menos de su precio, y nadie se entera hasta que alguien lo
 *     compra por un dólar.
 *
 * La regla nueva: el ÚLTIMO punto o coma es el decimal sólo si deja una o dos
 * cifras detrás. Cualquier otro es de millares y se descarta.
 */
export function toMinorUnits(value: string, currency: string): number | null {
  const clean = value.replace(/[^\d.,]/g, '')
  if (!clean || !/\d/.test(clean)) return null
  const cut = Math.max(clean.lastIndexOf('.'), clean.lastIndexOf(','))
  const decimals = cut < 0 ? 0 : clean.length - cut - 1
  const normalized = cut >= 0 && decimals > 0 && decimals <= 2
    ? `${clean.slice(0, cut).replace(/[.,]/g, '')}.${clean.slice(cut + 1)}`
    : clean.replace(/[.,]/g, '')
  const amount = Number(normalized)
  if (!Number.isFinite(amount) || amount < 0) return null
  const factor = ZERO_DECIMAL.has(currency.toLowerCase()) ? 1 : 100
  return Math.round(amount * factor)
}

/**
 * Si publicar esta oferta supone un cobro en línea.
 *
 * El mismo `chargesOnline` del worker, escrito aquí para saber si pedir Stripe
 * antes de dejar pulsar «Publicar». La reja de verdad está allí
 * (`assertCanPublish`); esto evita el viaje y explica por qué, que es lo que un
 * 409 a secas no hace.
 *
 * La excepción es la reserva que se paga en el lugar —o que no se cobra, que en
 * este modelo es lo mismo—: ésa no pasa por Stripe ni publicada, y exigirle
 * cuenta dejaría sin agenda a quien sólo quiere que le aparten hora.
 */
export function chargesOnline(offer: Offer): boolean {
  const isBooking = offer.booking.durationMinutes !== null
  return !(isBooking && offer.booking.paymentMode === 'onsite') && !isFree(offer)
}

/**
 * Si el catálogo tiene al menos una oferta que se cobra en línea: con precio
 * mayor que cero y sin ser una cita que se paga en el lugar.
 *
 * Es lo que decide si tiene sentido pedir que se conecte Stripe. Sin nada que
 * cobrar, el aviso de «todavía no puedes cobrar» sólo estorba a quien está
 * montando un sitio que no vende, o que aún no ha llegado a esa parte.
 */
export function hasPaidOffer(offers: Offer[]): boolean {
  return offers.some((offer) => chargesOnline(offer) && offer.prices.some((price) => price.active && price.unitAmount > 0))
}

/**
 * Una oferta que no cuesta nada. El mismo `isFree` del worker.
 *
 * Cero es un PRECIO y no la falta de uno: sin precio la oferta no se puede
 * vender —el botón revienta al pulsarlo— y gratis sí se entrega. Esa es toda la
 * diferencia entre «aún no lo he decidido» y «no cuesta nada».
 *
 * Sólo para lo que no es agenda: una cita sin coste ya lo dice con su modo de
 * cobro, y dos caminos para el mismo caso son dos sitios donde arreglar el mismo
 * fallo el día que aparezca.
 */
export function isFree(offer: Offer): boolean {
  if (offer.booking.durationMinutes !== null) return false
  const active = offer.prices.filter((price) => price.active)
  const price = active.find((row) => row.isDefault) ?? active[0]
  return price !== undefined && price.unitAmount === 0
}

/**
 * Por qué no se puede publicar esta oferta, o `null` si sí se puede.
 *
 * Es la regla del servidor —`becomesChargeable` + `assertCanPublish`— traída
 * aquí para poder DECIRLA antes de que alguien pulse. Las tres salidas:
 *
 * 1. Lo que no cobra en línea se publica siempre. Ver `chargesOnline`.
 * 2. Mientras no se sepa el estado de la cuenta no se bloquea nada: el servidor
 *    sigue decidiendo, y un fallo de red nuestro no puede impedirle publicar a
 *    quien sí puede. Por eso `stripeReady === false` y no `!stripeReady`.
 * 3. Sin cuenta lista, el motivo depende del plan: en gratis ni siquiera se
 *    puede conectar —`assertCanConnectStripe` devuelve 402— así que lo que falta
 *    es la membresía, no la cuenta. Ofrecer «Conectar Stripe» ahí sería mandar a
 *    una puerta cerrada.
 */
/**
 * Por qué no se puede publicar esto, si es que no se puede.
 *
 * `stripe` y `plan` son la misma pregunta —«¿puedes cobrarlo?»— y `limit` es
 * otra: «¿te caben más publicadas?». Se distinguen porque se resuelven distinto
 * y se cuentan distinto: una se arregla conectando una cuenta y la otra
 * retirando algo o subiendo de plan. `limit` lleva el plan encima para que
 * quien lo pinte no tenga que pedirlo por su cuenta.
 */
export type PublishBlock =
  | { kind: 'plan' | 'stripe' }
  | { kind: 'limit'; plan: BillingPlan }
  | null

export function publishBlockFor(
  offer: Offer,
  { stripeReady, canConnectStripe, plan, publishedOffers }: {
    stripeReady: boolean | null
    canConnectStripe: boolean
    /** El plan del proyecto y cuántas ofertas publicadas tiene ya. */
    plan: BillingPlan
    publishedOffers: number
  },
): PublishBlock {
  /*
   * El cupo, antes que nada.
   *
   * Va primero porque no depende de Stripe: una oferta gratuita o una reserva
   * que se paga en el lugar no pasan por caja —y ahí abajo se devolvería `null`
   * sin mirar nada más— pero ocupan hueco igual que las demás. Y ya publicada
   * no se comprueba: ésa ya está contada, y volver a mirarla impediría
   * retirarla y volverla a poner.
   */
  if (offer.status !== 'active' && reachedLimit(publishedOffers, PLAN_LIMITS[plan].offers)) {
    return { kind: 'limit', plan }
  }
  if (!chargesOnline(offer)) return null
  if (stripeReady !== false) return null
  return canConnectStripe ? { kind: 'stripe' } : { kind: 'plan' }
}

/**
 * La clave de catálogo de cada cosa, no su texto.
 *
 * Son constantes de módulo: no pueden llamar a `useI18n`, así que guardan la
 * clave y quien las use la pasa por su propio `t`. Un mapa con el texto ya
 * resuelto se congelaría en el idioma que hubiera al cargar el archivo, que es
 * exactamente el fallo que se ve cuando alguien cambia de idioma y media
 * pantalla no le sigue.
 */
/**
 * El dibujo de un archivo, según lo que sea.
 *
 * Un `.png` con un icono de PDF es una mentira pequeña pero constante: se ve en
 * cada fila y en cada asistente. Se elige por el tipo declarado, que es lo que
 * el navegador mandó al subirlo, y se cae a la hoja genérica cuando no dice
 * nada — un archivo sin tipo es un archivo, no un PDF.
 *
 * Devuelve el componente, no el elemento: este archivo no es `.tsx` y no tiene
 * por qué serlo para decidir un icono.
 */
export function fileIconFor(contentType: string): typeof IconFile {
  if (contentType === 'application/pdf') return IconFileTypePdf
  if (contentType.startsWith('image/')) return IconPhoto
  if (contentType.startsWith('video/')) return IconVideo
  if (contentType.startsWith('audio/')) return IconMusic
  if (contentType.includes('zip') || contentType.includes('compressed')) return IconFileZip
  if (contentType.includes('csv') || contentType.includes('spreadsheet')) return IconFileTypeCsv
  if (contentType.includes('word') || contentType.includes('document')) return IconFileTypeDoc
  return IconFile
}

/** «2,4 MB», para enseñarlo junto al nombre. No sirve para calcular nada. */
export function fileSizeLabel(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Qué es cada oferta en la lista, con la agenda por delante del tipo.
 *
 * `TYPE_KEY` sigue siendo el tipo tal cual —lo que se elige en el alta y en la
 * ficha— y esto es la lectura de la lista, que no es lo mismo: una oferta de
 * tipo `service` con agenda se busca como «Reserva», no como «Servicio».
 * Separarlos es lo que permite que la etiqueta diga lo que la gente piensa sin
 * tocar lo que el modelo guarda.
 */
/**
 * A dónde va el comprador, en una palabra.
 *
 * El resumen de la ficha necesita decirlo en el ancho de un recuadro, y las
 * etiquetas del selector —«Página incluida», «Una página de mi sitio»— están
 * escritas para elegir, no para leer de un vistazo.
 */
export const THANK_YOU_KEY: Record<Offer['thankYouMode'], string> = {
  default: 'offers.detail.thankYou.shortDefault',
  page: 'offers.detail.thankYou.shortPage',
  url: 'offers.detail.thankYou.shortUrl',
}

export type OfferKind = Offer['type'] | 'booking'

export const KIND_ICON = {
  digital: IconFileDescription,
  service: IconBriefcase,
  course: IconSchool,
  physical: IconPackage,
  booking: IconCalendarEvent,
} as const

export const KIND_KEY: Record<OfferKind, string> = {
  digital: 'offers.kind.digital',
  service: 'offers.kind.service',
  course: 'offers.kind.course',
  physical: 'offers.kind.physical',
  booking: 'offers.kind.booking',
}

export function kindOf(offer: Offer): OfferKind {
  return offer.booking.durationMinutes !== null ? 'booking' : offer.type
}

/**
 * Las cadencias que se pueden elegir, en el orden en que se piensan.
 *
 * Mensual y anual son las dos que usa casi todo el mundo y van primero; las
 * otras tres existen porque hay negocios que cobran así y no tenerlas obliga a
 * inventarse un apaño —cobrar mensual y cancelar a mano—. Más de cinco sería
 * ruido: cada opción de más es una decisión más justo antes de pagar.
 *
 * `count` es lo que las hace posibles sin unidades nuevas: un trimestre es
 * `month` tres veces, que es exactamente como lo entiende Stripe.
 */
export const CADENCES = [
  { id: 'month', interval: 'month' as const, count: 1, labelKey: 'offers.cadence.month' },
  { id: 'year', interval: 'year' as const, count: 1, labelKey: 'offers.cadence.year' },
  { id: 'quarter', interval: 'month' as const, count: 3, labelKey: 'offers.cadence.quarter' },
  { id: 'half', interval: 'month' as const, count: 6, labelKey: 'offers.cadence.half' },
  { id: 'week', interval: 'week' as const, count: 1, labelKey: 'offers.cadence.week' },
  { id: 'biweek', interval: 'week' as const, count: 2, labelKey: 'offers.cadence.biweek' },
] as const

export type CadenceId = (typeof CADENCES)[number]['id']

/**
 * Cómo se llama la recurrencia de un precio ya guardado.
 *
 * No basta `INTERVAL_KEY`: «mes» y «cada tres meses» son el mismo `interval` y
 * se distinguen sólo por el número, así que la etiqueta se busca por la pareja.
 * Sin pareja conocida se cae a la unidad a secas, que sigue siendo cierta.
 */
export function cadenceKeyOf(price: Pick<OfferPrice, 'interval' | 'intervalCount'>): string {
  const found = CADENCES.find((row) => row.interval === price.interval && row.count === (price.intervalCount || 1))
  return found?.labelKey ?? INTERVAL_KEY[price.interval]
}

/**
 * La cadencia de un precio ya guardado, para volver a ponerla en el asistente.
 *
 * El camino de vuelta de `CADENCES`: al editar hay que enseñar elegido lo que se
 * eligió, y lo guardado es la pareja (unidad, número) — «cada 3 meses», no
 * «trimestral». Sin pareja conocida devuelve null, que quien llama traduce por
 * su valor de partida: un precio con una recurrencia que este panel no sabe
 * nombrar no puede fingir que sí.
 */
export function cadenceIdOf(price: Pick<OfferPrice, 'interval' | 'intervalCount'>): CadenceId | null {
  return CADENCES.find((row) => row.interval === price.interval && row.count === (price.intervalCount || 1))?.id ?? null
}

export const INTERVAL_KEY: Record<OfferPrice['interval'], string> = {
  one_time: 'offers.interval.one_time',
  week: 'offers.interval.week',
  month: 'offers.interval.month',
  year: 'offers.interval.year',
}

/**
 * Cómo se llama cada estado, en la lista y dentro de la ficha.
 *
 * «Público» y no «A la venta» porque es lo que de verdad decide el estado: si
 * alguien que entra en el sitio la ve o no. «A la venta» además se contradecía
 * con una oferta publicada y sin precio, que existe y es un caso normal. Y hace
 * pareja con los botones de la ficha, que dicen Publicar y Despublicar.
 */
export const STATUS_KEY: Record<Offer['status'], string> = {
  draft: 'offers.status.draft',
  active: 'offers.status.active',
  archived: 'offers.status.archived',
}

/**
 * El estado de una venta, en una palabra.
 *
 * Tres y no dos porque devolver la mitad no es ni pagado ni reembolsado, y son
 * los tres que escribe el webhook del reembolso. Vive aquí para que la lista, el
 * widget de inicio y la ficha del cobro no acaben llamándolo cada uno de una
 * manera.
 */
export const ORDER_STATUS_KEY: Record<string, string> = {
  paid: 'orders.status.paid',
  partially_refunded: 'orders.status.partial',
  refunded: 'orders.status.refunded',
}

export function orderStatusLabel(status: string, t: Translate): string {
  return t(ORDER_STATUS_KEY[status] ?? 'orders.status.refunded')
}

/** El estado del envío de un producto físico, como se lee en la lista y en la ficha. */
export const FULFILLMENT_KEY: Record<FulfillmentStatus, string> = {
  unfulfilled: 'orders.fulfillment.unfulfilled',
  shipped: 'orders.fulfillment.shipped',
  delivered: 'orders.fulfillment.delivered',
  ready_for_pickup: 'orders.fulfillment.ready_for_pickup',
  picked_up: 'orders.fulfillment.picked_up',
}

/** Ámbar lo que falta por hacer, gris lo que va de camino, verde lo terminado. */
export function fulfillmentTone(status: FulfillmentStatus): string {
  if (status === 'unfulfilled') return 'is-partial'
  return status === 'shipped' || status === 'ready_for_pickup' ? 'is-review' : 'is-active'
}

/**
 * Si un pedido espera a que el negocio lo envíe o lo prepare. Sólo lo pagado:
 * uno reembolsado ya no se manda.
 */
export function awaitsShipping(order: Pick<Order, 'status' | 'fulfillmentStatus'>): boolean {
  return order.fulfillmentStatus === 'unfulfilled' && order.status !== 'refunded'
}

/** La cápsula que le toca: verde sólo lo cobrado, ámbar lo devuelto a medias. */
export function orderStatusTone(status: string): string {
  if (status === 'paid') return 'is-active'
  return status === 'partially_refunded' ? 'is-partial' : 'is-draft'
}

export const TYPE_KEY: Record<Offer['type'], string> = {
  digital: 'offers.type.digital',
  service: 'offers.type.service',
  course: 'offers.type.course',
  physical: 'offers.type.physical',
}

/**
 * El precio que representa a la oferta allí donde sólo cabe uno.
 *
 * El recomendado si lo hay; si no, el primero que siga vivo. Una oferta puede
 * tener varios precios y la lista no es sitio para enseñarlos todos.
 */
export function mainPrice(offer: Offer): OfferPrice | null {
  return offer.prices.find((price) => price.isDefault && price.active)
    ?? offer.prices.find((price) => price.active)
    ?? null
}

/**
 * Lo generado por la oferta allí donde sólo cabe una línea.
 *
 * Manda la moneda que más facturó —el servidor las manda ya ordenadas—, que es
 * la misma salida que toman las analíticas de venta: dos monedas no se suman en
 * un número, pero tampoco caben las dos en una celda de tabla. `rest` dice
 * cuántas se están quedando fuera para que la pantalla pueda decirlo en vez de
 * esconderlas.
 */
export function mainRevenue(offer: Offer): { line: OfferRevenue; rest: OfferRevenue[] } | null {
  const [line, ...rest] = offer.revenue
  return line ? { line, rest } : null
}

/**
 * Todo lo generado, escrito en una línea por moneda.
 *
 * Es lo que va en el `title` de la celda y en la ficha: donde una sola cifra
 * mentiría por omisión, se enumeran todas.
 */
export function revenueSummary(offer: Offer, t: Translate): string {
  if (offer.revenue.length === 0) return t('offers.noRevenue')
  return offer.revenue.map((line) => line.formatted).join(' · ')
}

/**
 * El detalle técnico de un fallo, para enseñarlo tal cual.
 *
 * `errorMessage` existe para el usuario: traduce cualquier cosa a "algo salió
 * mal". Eso está bien cuando el fallo es de él —un precio mal escrito— y es
 * inútil cuando algo está roto de verdad: deja la pantalla diciendo "no pudimos
 * cargar" sin que nadie, ni el que lo escribió, pueda saber por qué.
 *
 * `request` lanza `${status}: ${cuerpo}`, así que aquí se separan y se saca el
 * mensaje del servidor si el cuerpo era JSON.
 */
export function failureDetail(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error)
  const match = /^(\d{3}):\s*([\s\S]*)$/.exec(raw)
  if (!match) return raw.slice(0, 300)
  const status = match[1] ?? ''
  const body = (match[2] ?? '').trim()
  try {
    const parsed = JSON.parse(body) as { error?: string; message?: string }
    const message = parsed.error ?? parsed.message
    if (message) return `${status} — ${message}`
  } catch { /* el cuerpo no era JSON: se usa crudo */ }
  return body ? `${status} — ${body.slice(0, 300)}` : `Error ${status}`
}

/**
 * Lo que se le enseña a alguien cuando el servidor dice que no.
 *
 * Pasa por `translateServerError`, que es el único que sabe traducir: si la
 * respuesta trae un `code`, se dice en el idioma del panel; si no, se cae al
 * texto del servidor. Antes esto extraía el mensaje por su cuenta y lo pintaba
 * tal cual, así que un panel en inglés contestaba en español en cuanto algo
 * fallaba — y añadir códigos en el worker no habría cambiado nada mientras las
 * pantallas siguieran leyendo el texto a mano.
 */
export function errorMessage(error: unknown): string {
  return translateServerError(error, translate('error.unexpected'))
}

/**
 * Un importe en la unidad mínima, en la moneda en que se cobró.
 *
 * Aquí y no en cada pantalla: el saldo se enseña en el widget de inicio, en la
 * sección de Depósitos y en las analíticas de ventas, y dos formateadores para
 * el mismo número acaban diferenciándose en el símbolo o en los decimales justo
 * cuando alguien compara dos pantallas.
 *
 * `compact` abrevia —«$1.3 M»— para el eje de una gráfica, donde la cifra se
 * repite tres veces y le disputa el ancho a la propia línea.
 */
export function formatMoney(amount: number, currency: string, options?: { compact?: boolean }): string {
  const upper = (currency || 'mxn').toUpperCase()
  /* Dividir siempre entre cien enseñaba mil yenes como «¥10»: en las monedas
     sin decimales el importe entero YA es la unidad. */
  const value = amount / minorUnitFactor(currency)
  try {
    /* El locale sale de la región del proyecto: quien cobra en euros escribe
       «1.200,00 €», no «€1,200.00». Va por variable de módulo y no por
       parámetro porque esto se llama también desde ejes de gráfica y celdas de
       tabla — el porqué largo está en `lib/region.tsx`. */
    return new Intl.NumberFormat(moneyLocale(), {
      style: 'currency',
      currency: upper,
      ...(options?.compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : {}),
    }).format(value)
  } catch {
    // Una moneda que `Intl` no conoce no puede tirar la pantalla entera.
    return `${value.toFixed(2)} ${upper}`
  }
}

/**
 * El mismo importe, partido en unidad y cifra.
 *
 * Existe para las tarjetas de cifra grande. `USD 2,226.00` a tamaño de titular
 * no cabe junto a su insignia de tendencia y se cortaba con puntos suspensivos
 * — y lo que se perdía eran los pesos, no los centavos. Separando el código de
 * moneda se puede escribir pequeño, que es donde sobra sitio, y el número
 * recupera el ancho que le hacía falta.
 *
 * `unitFirst` porque la posición la decide la moneda y el idioma, no nosotros:
 * `$2,226` lleva el símbolo delante y hay monedas que lo llevan detrás.
 */
export function formatMoneyParts(
  amount: number,
  currency: string,
  options?: { compact?: boolean },
): { unit: string; amount: string; unitFirst: boolean } {
  const upper = (currency || 'mxn').toUpperCase()
  const value = amount / minorUnitFactor(currency)
  try {
    const formatter = new Intl.NumberFormat(moneyLocale(), {
      style: 'currency',
      currency: upper,
      ...(options?.compact ? { notation: 'compact' as const, maximumFractionDigits: 1 } : {}),
    })
    const parts = formatter.formatToParts(value)
    const at = parts.findIndex((part) => part.type === 'currency')
    if (at < 0) return { unit: '', amount: formatter.format(value), unitFirst: true }
    return {
      unit: parts[at]?.value ?? '',
      // El resto entero, sin el hueco que separaba a la unidad del número: ese
      // espacio lo pone luego el maquetado, y aquí sólo dejaría un sangrado.
      amount: parts.filter((_, index) => index !== at).map((part) => part.value).join('').trim(),
      unitFirst: at === 0,
    }
  } catch {
    return { unit: upper, amount: value.toFixed(2), unitFirst: true }
  }
}

/**
 * Cuándo llega el próximo depósito, en las palabras en que se piensa.
 *
 * «El martes» y no «19 de agosto»: a tres días vista nadie traduce la fecha, y
 * el dato que se busca aquí es cuánto falta. A partir de una semana ya no hay
 * día de la semana que valga —«el martes» sería ambiguo— y vuelve la fecha.
 *
 * Stripe da la llegada en segundos y en UTC; el `Date` la pasa a la zona del
 * navegador, que es donde vive quien lo lee.
 */
export function payoutArrival(arrivalDate: number, t: Translate): string {
  const when = new Date(arrivalDate * 1_000)
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const days = Math.round((startOfDay(when) - startOfDay(new Date())) / 86_400_000)
  if (days <= 0) return t('payouts.arrival.today')
  if (days === 1) return t('payouts.arrival.tomorrow')
  /* El día de la semana y la fecha corta son del PANEL, no del negocio: los lee
     quien trabaja, así que siguen su idioma y no el de la región. El `'es-MX'`
     que había aquí escribía «martes» dentro de un panel en inglés. */
  const locale = activeLanguage()
  if (days < 7) return when.toLocaleDateString(locale, { weekday: 'long' })
  return when.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}
